import { apiClient } from './apiClient';

/**
 * Provider service for API operations
 */
export class ProviderService {
  constructor() {
    this.BASE_URL = '/providers';
  }

  async fetchProviders(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.firstName) queryParams.append("firstName", params.firstName);
      if (params.lastName) queryParams.append("lastName", params.lastName);
      if (params.npi) queryParams.append("npi", params.npi);
      if (params.name) queryParams.append("name", params.name);
      if (params.providerName) queryParams.append("name", params.providerName);
      // Entity filter support for UnifiedClaimsFilter (firstName/lastName)
      if (params.contact) queryParams.append("contactName", params.contact);
      if (params.contactName) queryParams.append("contactName", params.contactName);
      if (params.speciality) queryParams.append("speciality", params.speciality);
      if (params.classification) queryParams.append("classification", params.classification);
      if (params.postalCode) queryParams.append("postalCode", params.postalCode);
      if (params.latitude) queryParams.append("latitude", params.latitude.toString());
      if (params.longitude) queryParams.append("longitude", params.longitude.toString());
      if (params.miles) queryParams.append("distanceMiles", params.miles.toString());
      if (params.networkType) queryParams.append("networkType", params.networkType.toString());
      if (params.ratingRange) queryParams.append("rating", params.ratingRange.toString());

      const url = `${this.BASE_URL}/search?${queryParams.toString()}`;
      const response = await apiClient.post(url);

      return response;
    } catch (error) {
      console.error("Error fetching providers. Check network tab or server logs.");
      throw error;
    }
  }

  /**
   * Get provider details by ID
   * @param {string} id - Provider ID
   * @returns {Promise<Object>} Provider details
   */
  async getProviderById(id) {
    if (!id) throw new Error('Provider ID is required.');
    const response = await apiClient.get(`${this.BASE_URL}/${id}`);
    return response;
  }

  /**
   * Create a new provider
   * @param {Object} payload - Provider data
   * @returns {Promise<Object>} Created provider
   */
  async createProvider(payload) {
    const response = await apiClient.post(this.BASE_URL, payload);
    return response;
  }

  /**
   * Update an existing provider
   * @param {string} id - Provider ID
   * @param {Object} payload - Updated provider data
   * @returns {Promise<Object>} Updated provider
   */
  async updateProvider(id, payload) {
    if (!id) throw new Error('Provider ID is required to update provider.');
    const response = await apiClient.put(`${this.BASE_URL}/${id}`, payload);
    return response;
  }

  /**
   * Soft delete a provider
   * @param {string} id - Provider ID
   * @returns {Promise<Object>} Delete response
   */
  async deleteProvider(id) {
    if (!id) throw new Error('Provider ID is required to delete provider.');
    const response = await apiClient.delete(`${this.BASE_URL}/${id}`);
    return response;
  }

  async searchFallbackProviders(payload) {
    const response = await apiClient.post(`${this.BASE_URL}/fallback-search`, payload);
    return response;
  }

  async importFallbackProvider(payload) {
    const response = await apiClient.post(`${this.BASE_URL}/import-fallback`, payload);
    return response;
  }

  async searchExternalProviders(payload = {}) {
    const response = await apiClient.post(`${this.BASE_URL}/external-search`, payload);
    return response;
  }

  async searchInternalLawyersByArea(payload = {}) {
    const response = await apiClient.post(`${this.BASE_URL}/internal-lawyers-search`, payload);
    return response;
  }

  async geocodeAddress(payload = {}) {
    const response = await apiClient.post(`${this.BASE_URL}/geocode-address`, payload);
    return response;
  }

  async mergeProviders(payload) {
    try {
      const response = await apiClient.post(`${this.BASE_URL}/merge`, payload);
      return response;
    } catch (error) {
      console.error('Error merging providers:', error);
      throw error;
    }
  }

  async validateMerge(sourceId) {
    try {
      const response = await apiClient.get(`${this.BASE_URL}/merge-validate?sourceId=${sourceId}`);
      return response;
    } catch (error) {
      console.error('Error validating provider merge:', error);
      throw error;
    }
  }

}

// Singleton instance
export const providerService = new ProviderService();
